import React, { useState } from 'react';

import { ActionIcon, Avatar, Flex, Menu, Paper, PaperProps, Text, useMantineTheme } from '@mantine/core';
import {
  IconDots,
  IconMessages,
  IconPhoneIncoming,
  IconShare2,
  IconTag,
  IconTrash,
  IconUsers,
  IconUsersGroup,
  IconWand,
  IconWorldUpload
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AddSmartAssistantToStoreButton } from 'components/AddSmartAssistantToStoreButton';
import { EmbedSmartAssistantWebsiteModal } from 'components/EmbedSmartAssistantWebsiteModal';
import { PauseSmartAssistantButton } from 'components/PauseSmartAssistantButton';
import { ShareSmartAssistantModal } from 'components/ShareSmartAssistantModal';
import { ItemTagsView } from 'components/TagComponents/ItemTagsView';
import { TagEditModal } from 'components/TagComponents/TagEditModal';
import { TeamAccessModal } from 'components/TeamAccessModal';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useUserContext } from 'contexts/UserContext';
import { GitbookIcon } from 'hugeicons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { NotificationService } from 'services/notification-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { UserService } from 'services/user-service';
import { SMART_ASSISTANT_DELETE_NOTIF_ID } from 'shared/constants/notification-ids';
import { SMART_ASSISTANTS_QUERY_KEY } from 'shared/constants/query-keys';
import { ManageSmartAssistantTabSlug, ROUTE_LINKS } from 'shared/constants/route-links';
import { MY_SMART_ASSISTANTS_FILTER } from 'shared/constants/smart-assistant';
import { CARD_HEIGHT, CARD_WIDTH } from 'shared/constants/theme';
import { SMART_ASSISTANT_CONFIGURABLE_TOOL_GROUPS_BY_TYPE } from 'shared/constants/tool-group';
import { SmartAssistant, SmartAssistantType } from 'shared/interfaces/SmartAssistant';
import { ToolKind } from 'shared/interfaces/tools/Tool';
import { UserAccess } from 'shared/interfaces/User';
import { SMART_ASSISTANT_TYPE_DATA, canPauseSmartAssistant } from 'shared/utils/smart-assistant';

interface SmartAssistantCardProps extends PaperProps {
  smartAssistant: SmartAssistant;
  selectedTeamFilter?: string;
  allTags?: string[];
}

export function SmartAssistantCard({
  smartAssistant,
  selectedTeamFilter = MY_SMART_ASSISTANTS_FILTER,
  allTags = [],
  ...paperProps
}: SmartAssistantCardProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [user] = useUserContext();
  const [subscription] = useSubscriptionContext();

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [teamAccessModalOpen, setTeamAccessModalOpen] = useState<boolean>(false);
  const [editTagModalOpen, setEditTagModalOpen] = useState(false);
  const [shareSmartAssistantModalOpen, setShareSmartAssistantModalOpen] = useState(false);
  const [embedSmartAssistantWebsiteModalOpen, setEmbedSmartAssistantWebsiteModalOpen] = useState(false);

  const manageSmartAssistant = () => {
    navigate(`${ROUTE_LINKS.manageSmartAssistant}/${smartAssistant._id}`);
  };

  const navigateToToolsSection = () => {
    navigate(`${ROUTE_LINKS.manageSmartAssistant}/${smartAssistant._id}/${ManageSmartAssistantTabSlug.TOOLS}`);
  };

  const navigateToConvesationsSection = () => {
    navigate(`${ROUTE_LINKS.manageSmartAssistant}/${smartAssistant._id}/${ManageSmartAssistantTabSlug.CONVERSATIONS}`);
  };

  const navigateToVoiceCallsSection = () => {
    navigate(`${ROUTE_LINKS.manageSmartAssistant}/${smartAssistant._id}/${ManageSmartAssistantTabSlug.VOICE_CALLS}`);
  };

  const navigateToLeadsSection = () => {
    navigate(`${ROUTE_LINKS.manageSmartAssistant}/${smartAssistant._id}/${ManageSmartAssistantTabSlug.LEADS}`);
  };

  // Open/Close modals
  const openTeamAccesModal = () => setTeamAccessModalOpen(true);
  const closeTeamAccessModal = () => setTeamAccessModalOpen(false);
  const openEditTagsModal = () => setEditTagModalOpen(true);
  const closeEditTagsModal = () => setEditTagModalOpen(false);
  const openShareSmartAssistantModal = () => setShareSmartAssistantModalOpen(true);
  const closeShareSmartAssistantModal = () => setShareSmartAssistantModalOpen(false);
  const openEmbedSmartAssistantWebsiteModal = () => setEmbedSmartAssistantWebsiteModalOpen(true);
  const closeEmbedSmartAssistantWebsiteModal = () => setEmbedSmartAssistantWebsiteModalOpen(false);

  // Mutations
  const deleteSmartAssistantMutation = useMutation({
    mutationFn: SmartAssistantService.deleteSmartAssistant,
    onMutate: () =>
      NotificationService.startAction(SMART_ASSISTANT_DELETE_NOTIF_ID, 'FEED_BACK.SMART_ASSISTANT.LOADING.DELETE'),
    onSuccess: (deletedSmartAssistantId: string) => {
      queryClient.setQueryData(
        [SMART_ASSISTANTS_QUERY_KEY, selectedTeamFilter ?? MY_SMART_ASSISTANTS_FILTER],
        (existingSmartAssistants: SmartAssistant[] = []) =>
          existingSmartAssistants.filter((smartAssistant) => smartAssistant._id !== deletedSmartAssistantId)
      );
      NotificationService.endAction(true, SMART_ASSISTANT_DELETE_NOTIF_ID, 'FEED_BACK.SMART_ASSISTANT.SUCCESS.DELETE');
    },
    onError: () =>
      NotificationService.endAction(false, SMART_ASSISTANT_DELETE_NOTIF_ID, 'FEED_BACK.SMART_ASSISTANT.FAILURE.DELETE')
  });

  return (
    <>
      <Paper
        p={16}
        withBorder
        w={CARD_WIDTH}
        h={CARD_HEIGHT}
        className="g-hover-style"
        style={{ position: 'relative' }}
        {...paperProps}
        onClick={manageSmartAssistant}>
        <Flex direction="column" justify="space-between" align="start" w="100%" h="100%">
          <Flex direction="row" align="center" justify="flex-start" gap={10}>
            <Avatar src={smartAssistant.avatarUrl} radius={20} size={40}>
              {smartAssistant.name[0].toUpperCase()}
            </Avatar>
            <Flex direction="column" gap={5}>
              <Text fz="md" fw={500} truncate maw={CARD_WIDTH * 0.65}>
                {smartAssistant.name}
              </Text>
              <Flex direction="row" align="center" gap={6} sx={{ color: theme.other.text.dimmed }}>
                {SMART_ASSISTANT_TYPE_DATA[smartAssistant.type]?.icon(16)}
                <Text size="sm" color={theme.other.text.dimmed}>
                  {SMART_ASSISTANT_TYPE_DATA[smartAssistant.type]?.title}
                </Text>
              </Flex>
            </Flex>
          </Flex>

          <Flex
            w="100%"
            justify="space-between"
            align="center"
            pt={16}
            sx={{ borderTop: `1px solid ${theme.other.gray.border}` }}>
            <Flex direction="row" align="center" gap={8}>
              <GitbookIcon size={16} />
              <Text size="sm">
                {t('SMART_ASSISTANT.RESOURCE_NUMBER', {
                  number: smartAssistant.resources.length > 100 ? '+100' : smartAssistant.resources.length
                })}
              </Text>
            </Flex>

            {smartAssistant.creator === user._id && (
              <Menu
                position="right-start"
                withArrow
                width={190}
                arrowPosition="center"
                offset={0}
                opened={menuOpen}
                onClose={() => setMenuOpen(false)}>
                <Menu.Target>
                  <ActionIcon
                    onClick={(event) => {
                      event.stopPropagation();
                      setMenuOpen(true);
                    }}>
                    <IconDots color={theme.other.text.default} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown onClick={(event) => event.stopPropagation()}>
                  {smartAssistant.websiteIntegration && !smartAssistant.shopifyStoreResource && (
                    <Menu.Item icon={<IconWorldUpload />} onClick={openEmbedSmartAssistantWebsiteModal}>
                      {t('SMART_ASSISTANT.EMBED_WEBSITE.TITLE')}
                    </Menu.Item>
                  )}
                  {smartAssistant.shopifyStoreResource && subscription.product.smartAssistantIntegrations && (
                    <AddSmartAssistantToStoreButton
                      smartAssistant={smartAssistant}
                      menuItem
                      disabled={!!smartAssistant.shopifyStoreResource}
                    />
                  )}
                  {canPauseSmartAssistant(smartAssistant) && (
                    <PauseSmartAssistantButton smartAssistant={smartAssistant} menuItem />
                  )}
                  <Menu.Item icon={<IconShare2 />} onClick={openShareSmartAssistantModal}>
                    {t('APP.ACTIONS.SHARE')}
                  </Menu.Item>

                  {UserService.isCustomOrganisationMember() &&
                    UserService.hasUserAccessWithMinRole(UserAccess.ADMIN) && (
                      <Menu.Item icon={<IconUsersGroup />} onClick={openTeamAccesModal}>
                        {t('APP.ACTIONS.MANAGE_TEAM_ACCESS')}
                      </Menu.Item>
                    )}

                  {SMART_ASSISTANT_CONFIGURABLE_TOOL_GROUPS_BY_TYPE[smartAssistant.type].length > 0 && (
                    <Menu.Item icon={<IconWand />} onClick={navigateToToolsSection}>
                      {t('SMART_ASSISTANT.TOOLS_SECTION.TITLE')}
                    </Menu.Item>
                  )}
                  {smartAssistant.type === SmartAssistantType.PHONE ? (
                    <Menu.Item icon={<IconPhoneIncoming />} onClick={navigateToVoiceCallsSection}>
                      {t('SMART_ASSISTANT.VOICE_CALLS_SECTION.TITLE')}
                    </Menu.Item>
                  ) : (
                    <Menu.Item icon={<IconMessages />} onClick={navigateToConvesationsSection}>
                      {t('SMART_ASSISTANT.CONVERSATIONS_SECTION.TITLE')}
                    </Menu.Item>
                  )}

                  {smartAssistant.toolConfigs?.find((toolConfig) => toolConfig.kind === ToolKind.COLLECT_LEAD_INFO) && (
                    <Menu.Item icon={<IconUsers />} onClick={navigateToLeadsSection}>
                      {t('SMART_ASSISTANT.LEADS_SECTION.TITLE')}
                    </Menu.Item>
                  )}

                  <Menu.Item icon={<IconTag />} onClick={openEditTagsModal}>
                    {t('TAG.EDIT.TITLE')}
                  </Menu.Item>
                  <Menu.Item
                    icon={<IconTrash />}
                    color="red"
                    onClick={() => deleteSmartAssistantMutation.mutate(smartAssistant._id)}
                    disabled={smartAssistant.creator !== user._id}>
                    {t('APP.ACTIONS.DELETE')}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Flex>
        </Flex>
        <ItemTagsView
          style={{ position: 'absolute', right: 15, top: 48 }}
          maxChipWidth={90}
          showedTagsCount={1}
          chipSize="xs"
          tags={smartAssistant.tags}
        />
      </Paper>
      <TeamAccessModal
        selectedTeamFilter={selectedTeamFilter}
        smartAssistant={smartAssistant}
        teamAccessModalOpen={teamAccessModalOpen}
        closeTeamAccessModal={closeTeamAccessModal}
      />
      <TagEditModal
        tags={allTags}
        opened={editTagModalOpen}
        onClose={closeEditTagsModal}
        smartAssistant={smartAssistant}
        onEditDone={() => queryClient.invalidateQueries([SMART_ASSISTANTS_QUERY_KEY, selectedTeamFilter])}
      />
      <ShareSmartAssistantModal
        smartAssistant={smartAssistant}
        opened={shareSmartAssistantModalOpen}
        onClose={closeShareSmartAssistantModal}
        onUpdate={() => queryClient.invalidateQueries([SMART_ASSISTANTS_QUERY_KEY, selectedTeamFilter])}
      />
      <EmbedSmartAssistantWebsiteModal
        smartAssistant={smartAssistant}
        opened={embedSmartAssistantWebsiteModalOpen}
        onClose={closeEmbedSmartAssistantWebsiteModal}
      />
    </>
  );
}
